import { useEffect, useSyncExternalStore } from 'react';
import { request } from '../api/client';
import { useMessagesLive } from './messages';

/**
 * Profile pictures (1.12.3). Who has one, per company, is read once (/api/tenants/:t/pictures) and
 * kept up to date by the live stream ('picture': someone's new version, or null when removed).
 * Every face in Stitch (Messages, People, the menu, calls) shows the picture when there is one,
 * else the initials. A picture's address has its version in it, so the browser keeps it for good.
 */
export type PictureSize = 'large' | 'medium' | 'small';

export interface PhonePicture {
  tenant: string;
  extension: string;
  status: 'sent' | 'removed' | 'failed' | 'off';
  error?: string;
}

interface State {
  /** userId → version, across every company loaded. */
  versions: Record<string, string>;
  /** company → extension → userId (people with a picture). */
  extensions: Record<string, Record<string, string>>;
}

let state: State = { versions: {}, extensions: {} };
const listeners = new Set<() => void>();
function set(next: Partial<State>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function pictureUrl(userId: string, version: string, size: PictureSize = 'medium'): string {
  return `/api/pictures/${encodeURIComponent(userId)}/${encodeURIComponent(version)}/${size}`;
}

/** Someone's picture's version, or null. */
export function usePictureOf(userId: string | undefined | null): string | null {
  return useSyncExternalStore(
    subscribe,
    () => (userId ? (state.versions[userId] ?? null) : null),
    () => null
  );
}

/** The person signed in as an extension at a company, when they have a picture. */
export function usePictureOfExtension(tenant: string, extension: string | undefined | null): { userId: string; version: string } | null {
  const userId = useSyncExternalStore(
    subscribe,
    () => (extension ? (state.extensions[tenant.toLowerCase()]?.[extension] ?? null) : null),
    () => null
  );
  const version = usePictureOf(userId);
  return userId && version ? { userId, version } : null;
}

/** After saving or removing one here: shown at once (the stream says so to everyone else). */
export function setPicture(userId: string, version: string | null, where?: { tenant: string; extension: string | null }): void {
  const versions = { ...state.versions };
  if (version) versions[userId] = version;
  else delete versions[userId];
  let extensions = state.extensions;
  if (where?.extension) {
    const t = where.tenant.toLowerCase();
    const map = { ...(extensions[t] ?? {}) };
    if (version) map[where.extension] = userId;
    else delete map[where.extension];
    extensions = { ...extensions, [t]: map };
  }
  set({ versions, extensions });
}

const loaded = new Map<string, Promise<void>>();
async function load(tenant: string, again = false): Promise<void> {
  const t = tenant.toLowerCase();
  if (!again && loaded.has(t)) return loaded.get(t);
  const p = request<{ people: Record<string, string>; extensions: Record<string, string> }>('tenant', 'GET', `/api/tenants/${encodeURIComponent(t)}/pictures`)
    .then((r) => set({ versions: { ...state.versions, ...r.people }, extensions: { ...state.extensions, [t]: r.extensions } }))
    .catch(() => {
      loaded.delete(t);
    });
  loaded.set(t, p);
  return p;
}

/**
 * For a company's people while Stitch is open: whose pictures there are, and new ones as they come
 * (`live`: only for people in a company, whose Stitch has the live stream; Stitch staff read them once).
 */
export function usePicturesLive(tenant: string, on: boolean, live = on): void {
  const t = tenant.toLowerCase();
  useEffect(() => {
    if (on && t) void load(t);
  }, [on, t]);
  useMessagesLive((e) => {
    if (e.type === 'reset' || e.type === 'hello') {
      void load(t, true);
      return;
    }
    if (e.type !== 'picture' || e.tenant?.toLowerCase() !== t || !e.userId) return;
    const versions = { ...state.versions };
    if (e.version) versions[e.userId] = e.version;
    else delete versions[e.userId];
    set({ versions });
    // Which extension is theirs: read again (cheap, and rare).
    void load(t, true);
  }, on && live);
}

// --- making the three sizes in the browser -------------------------------------------------------

export const SIZES: Record<PictureSize, number> = { large: 512, medium: 250, small: 150 };
const LIMITS: Record<PictureSize, number> = { large: 400 * 1024, medium: 160 * 1024, small: 80 * 1024 };

/** The part of the picture to keep: a square, in the image's own pixels. */
export interface Crop {
  x: number;
  y: number;
  size: number;
}

async function jpeg(img: CanvasImageSource, crop: Crop, size: number, limit: number): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('This browser can’t make the picture. Try another browser.');
  // A see-through picture (a PNG) gets white behind it, as JPEGs have no see-through parts.
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, size, size);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, crop.x, crop.y, crop.size, crop.size, 0, 0, size, size);
  for (const quality of [0.88, 0.8, 0.7, 0.6]) {
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', quality));
    if (!blob) throw new Error('This browser can’t make the picture. Try another browser.');
    if (blob.size <= limit) {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      let bin = '';
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return btoa(bin);
    }
  }
  throw new Error('That picture is too detailed to save. Try another.');
}

/** The cropped square at 512, 250 and 150 pixels, as JPEG (base64), as Stitch takes them. */
export async function pictureFiles(img: CanvasImageSource, crop: Crop): Promise<Record<PictureSize, string>> {
  return {
    large: await jpeg(img, crop, SIZES.large, LIMITS.large),
    medium: await jpeg(img, crop, SIZES.medium, LIMITS.medium),
    small: await jpeg(img, crop, SIZES.small, LIMITS.small)
  };
}

// --- saving -----------------------------------------------------------------------------------------

export interface PictureSaved {
  version: string;
  phones: PhonePicture[];
}

const personPath = (tenant: string, userId: string) => `/api/tenants/${encodeURIComponent(tenant)}/people/${encodeURIComponent(userId)}/picture`;

export const picturesApi = {
  mine: () => request<{ version: string | null; phones: PhonePicture[] }>('tenant', 'GET', '/api/me/picture'),
  saveMine: (files: Record<PictureSize, string>) => request<PictureSaved>('tenant', 'PUT', '/api/me/picture', files),
  removeMine: () => request<{ removed: boolean; phones: PhonePicture[] }>('tenant', 'DELETE', '/api/me/picture'),
  retryMine: () => request<{ phones: PhonePicture[] }>('tenant', 'POST', '/api/me/picture/phones'),
  theirs: (tenant: string, userId: string) => request<{ version: string | null; phones: PhonePicture[] }>('tenant', 'GET', personPath(tenant, userId)),
  saveTheirs: (tenant: string, userId: string, files: Record<PictureSize, string>) => request<PictureSaved>('tenant', 'PUT', personPath(tenant, userId), files),
  removeTheirs: (tenant: string, userId: string) => request<{ removed: boolean; phones: PhonePicture[] }>('tenant', 'DELETE', personPath(tenant, userId)),
  retryTheirs: (tenant: string, userId: string) => request<{ phones: PhonePicture[] }>('tenant', 'POST', `${personPath(tenant, userId)}/phones`)
};

/** How the picture is on the desk phones, in a line ("" when there's nothing to say). */
export function phoneWords(phones: PhonePicture[], whose: 'your' | 'their'): { text: string; tone: 'ok' | 'warn' | '' } {
  if (!phones.length) return { text: '', tone: '' };
  if (phones.some((p) => p.status === 'failed')) return { text: `It isn’t on ${whose} desk phone yet: the phone system didn’t take it.`, tone: 'warn' };
  if (phones.every((p) => p.status === 'off')) return { text: '', tone: '' };
  if (phones.some((p) => p.status === 'sent')) return { text: `It shows on ${whose} desk phone too, when ${whose === 'your' ? 'you call' : 'they call'} coworkers.`, tone: 'ok' };
  return { text: '', tone: '' };
}
